// src/compile.ts
import { z } from "zod";

// src/vet-transform.ts
import { parseExpressionAt } from "acorn";
var MATH_METHODS = /* @__PURE__ */ new Set([
  "min",
  "max",
  "abs",
  "round",
  "floor",
  "ceil",
  "trunc",
  "sign",
  "sqrt"
]);
var JSON_METHODS = /* @__PURE__ */ new Set(["stringify", "parse"]);
var GLOBAL_FNS = /* @__PURE__ */ new Set(["String", "Number"]);
var VALUE_MEMBERS = /* @__PURE__ */ new Set(["slice", "substring", "length"]);
var PAD_METHODS = /* @__PURE__ */ new Set(["padStart", "padEnd"]);
var MAX_PAD_LENGTH = 1024;
var REPLACE_METHOD = "replaceAll";
var APPROXIMATED_POWER = "implementation-approximated across engines";
var UNARY_OPS = /* @__PURE__ */ new Set(["-", "+", "!", "~", "typeof"]);
var MAX_TRANSFORM_SOURCE_LENGTH = 4096;
var MAX_TRANSFORM_DEPTH = 64;
var TOO_DEEP = `transform nests deeper than the limit of ${MAX_TRANSFORM_DEPTH} levels`;
var RESERVED_PREFIX = "__rowboat";
var VetError = class extends Error {
};
function checkNotReserved(name) {
  if (name.startsWith(RESERVED_PREFIX))
    throw new VetError(`identifier "${name}": the prefix "${RESERVED_PREFIX}" is reserved`);
}
function vetTransform(source, columns) {
  if (source.length > MAX_TRANSFORM_SOURCE_LENGTH)
    return {
      ok: false,
      error: `transform source is ${source.length} characters, over the limit of ${MAX_TRANSFORM_SOURCE_LENGTH}`
    };
  let arrow;
  try {
    arrow = parseExpressionAt(source, 0, { ecmaVersion: 2022 });
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    if (message.startsWith("Not enough stack space")) return { ok: false, error: TOO_DEEP };
    return { ok: false, error: `not a valid expression: ${message}` };
  }
  if (source.slice(arrow.end).trim() !== "")
    return { ok: false, error: "transform must be a single arrow expression" };
  if (arrow.type !== "ArrowFunctionExpression")
    return { ok: false, error: "transform must be an arrow function `($) => \u2026`" };
  if (arrow.async || arrow.generator)
    return { ok: false, error: "transform must be a plain (non-async) arrow function" };
  const params = arrow.params;
  if (params.length !== 1 || params[0].type !== "Identifier")
    return { ok: false, error: "transform must take a single parameter (the `$` context)" };
  const ctxParam = params[0].name;
  const body = arrow.body;
  if (body.type === "BlockStatement")
    return { ok: false, error: "transform must be an arrow expression (no statement body)" };
  const sources = [];
  const seen = /* @__PURE__ */ new Set();
  const addSource = (col) => {
    if (!seen.has(col)) {
      seen.add(col);
      sources.push(col);
    }
  };
  try {
    checkNotReserved(ctxParam);
    check(body, { ctx: ctxParam, columns, addSource, depth: 0 });
  } catch (e) {
    if (e instanceof VetError) return { ok: false, error: e.message };
    throw e;
  }
  return { ok: true, sources };
}
function check(node, w) {
  w.depth += 1;
  if (w.depth > MAX_TRANSFORM_DEPTH) throw new VetError(TOO_DEEP);
  checkNode(node, w);
  w.depth -= 1;
}
function checkNode(node, w) {
  const { ctx } = w;
  switch (node.type) {
    case "Literal":
      if (node.regex)
        throw new VetError("regular expressions are not allowed");
      if (typeof node.value === "bigint") throw new VetError("BigInt literals are not allowed");
      return;
    case "TemplateLiteral":
      for (const e of node.expressions) check(e, w);
      return;
    case "Identifier":
      if (node.name === ctx)
        throw new VetError(
          "the whole context object cannot be used as a value \u2014 reference specific columns (e.g. `$.name`)"
        );
      checkNotReserved(node.name);
      throw new VetError(`unknown identifier "${node.name}"`);
    case "MemberExpression":
      checkMember(node, w);
      if (!isColumnRead(node, ctx) && node.property.name !== "length")
        throw new VetError(
          `"${node.property.name}" is a built-in function and can only be called, not used as a value`
        );
      return;
    case "CallExpression": {
      const callee = node.callee;
      if (callee.type === "Identifier") {
        checkNotReserved(callee.name);
        if (!GLOBAL_FNS.has(callee.name))
          throw new VetError(`unsupported function "${callee.name}"`);
      } else if (callee.type === "MemberExpression") {
        const prop = callee.property;
        if (!callee.computed && prop.type === "Identifier" && PAD_METHODS.has(prop.name)) {
          checkPadLength(prop.name, node.arguments[0]);
          check(callee.object, w);
        } else if (!callee.computed && prop.type === "Identifier" && prop.name === REPLACE_METHOD) {
          checkReplaceAll(node.arguments);
          check(callee.object, w);
        } else {
          checkMember(callee, w);
        }
      } else {
        throw new VetError("unsupported call target");
      }
      for (const arg of node.arguments) check(arg, w);
      return;
    }
    case "BinaryExpression":
      if (node.operator === "**") throw new VetError(`"**" is ${APPROXIMATED_POWER}`);
      check(node.left, w);
      check(node.right, w);
      return;
    case "LogicalExpression":
      check(node.left, w);
      check(node.right, w);
      return;
    case "UnaryExpression":
      if (!UNARY_OPS.has(node.operator))
        throw new VetError(`unsupported unary operator "${node.operator}"`);
      check(node.argument, w);
      return;
    case "ConditionalExpression":
      check(node.test, w);
      check(node.consequent, w);
      check(node.alternate, w);
      return;
    default:
      throw new VetError(`unsupported syntax (${node.type})`);
  }
}
function isColumnRead(node, ctx) {
  const obj = node.object;
  return obj.type === "Identifier" && obj.name === ctx;
}
function checkMember(node, w) {
  const { ctx, columns, addSource } = w;
  if (node.computed) throw new VetError("computed member access is not allowed");
  const prop = node.property;
  if (prop.type !== "Identifier") throw new VetError("computed member access is not allowed");
  const name = prop.name;
  const obj = node.object;
  if (obj.type === "Identifier" && obj.name === ctx) {
    if (!columns.has(name)) throw new VetError(`unknown column "${name}"`);
    addSource(name);
    return;
  }
  if (obj.type === "Identifier" && obj.name === "Math") {
    if (name === "pow") throw new VetError(`"Math.pow" is ${APPROXIMATED_POWER}`);
    if (!MATH_METHODS.has(name)) throw new VetError(`unsupported member access "Math.${name}"`);
    return;
  }
  if (obj.type === "Identifier" && obj.name === "JSON") {
    if (!JSON_METHODS.has(name)) throw new VetError(`unsupported member access "JSON.${name}"`);
    return;
  }
  check(obj, w);
  if (PAD_METHODS.has(name)) throw new VetError(`.${name} must be called with a literal length`);
  if (name === REPLACE_METHOD)
    throw new VetError(`.${name} must be called with two string literals`);
  if (!VALUE_MEMBERS.has(name)) throw new VetError(`unsupported member access ".${name}"`);
}
function checkPadLength(method, arg) {
  const v = arg?.type === "Literal" ? arg.value : void 0;
  if (typeof v !== "number" || !Number.isInteger(v) || v < 0 || v > MAX_PAD_LENGTH)
    throw new VetError(`.${method} needs an integer literal length from 0 to ${MAX_PAD_LENGTH}`);
}
function checkReplaceAll(args) {
  const [pattern, replacement] = args;
  const isString = (n) => n?.type === "Literal" && typeof n.value === "string";
  if (args.length !== 2 || !isString(pattern) || !isString(replacement) || pattern.value === "")
    throw new VetError(`.${REPLACE_METHOD} needs two string literals and a non-empty pattern`);
  if (replacement.value.includes("$"))
    throw new VetError(`.${REPLACE_METHOD} replacement cannot contain "$" (a replacement pattern)`);
}

// src/compile.ts
function shapeOf(name, table) {
  const shape = table.shape;
  if (!shape) throw new Error(`table "${name}" must be a z.object`);
  return shape;
}
function rbOf(schema) {
  return schema.meta()?.rb;
}
function defType(schema) {
  return schema.def?.type;
}
function unwrapOnce(schema) {
  const fn = schema.unwrap;
  return typeof fn === "function" ? fn.call(schema) : void 0;
}
function resolveColumn(field) {
  let s = field;
  let optional = false;
  let rb2 = rbOf(field);
  while (s && (defType(s) === "optional" || defType(s) === "nullable")) {
    optional = true;
    s = unwrapOnce(s);
    if (s) rb2 ??= rbOf(s);
  }
  return { rb: rb2, optional, inner: s ?? field };
}
function emitJsonSchema(inner, where) {
  const out = z.toJSONSchema(inner, {
    unrepresentable: "any",
    override: ({ zodSchema, jsonSchema }) => {
      const def = zodSchema._zod.def;
      if (def.type === "union" && "discriminator" in def && typeof def.discriminator === "string")
        jsonSchema.discriminator = { propertyName: def.discriminator };
    }
  });
  delete out.rb;
  delete out.$schema;
  const ref2 = findRef(out);
  if (ref2 !== void 0)
    throw new Error(
      `${where}: the json schema lowers to $ref ${JSON.stringify(ref2)} (a recursive schema, or a sub-schema tagged with .meta({ id })), which the server validator does not resolve; inline the sub-schema, or model a tree as a table with rb.parent`
    );
  assertTaggedUnions(out, where);
  return lowerNestedOrdered(out, where);
}
function assertTaggedUnions(node, where) {
  const tag = tagOf(node);
  if (tag !== void 0) {
    for (const b of node.oneOf)
      if (!carriesTag(b, tag))
        throw new Error(
          `${where}: a branch of the z.discriminatedUnion on "${tag}" does not declare "${tag}" (a plain union, an intersection, or a nested discriminatedUnion with a branch missing it)`
        );
  }
  for (const s of subschemas(node)) assertTaggedUnions(s, where);
}
function tagOf(node) {
  const d = node.discriminator;
  return isNode(d) && typeof d.propertyName === "string" && Array.isArray(node.oneOf) ? d.propertyName : void 0;
}
function carriesTag(branch, tag) {
  if (!isNode(branch)) return false;
  if (isNode(branch.properties) && Object.hasOwn(branch.properties, tag)) return true;
  return tagOf(branch) !== void 0 && branch.oneOf.every((b) => carriesTag(b, tag));
}
function findRef(node) {
  if (node.$ref !== void 0) return node.$ref;
  for (const s of subschemas(node)) {
    const r = findRef(s);
    if (r !== void 0) return r;
  }
  return void 0;
}
function isNode(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
var SCHEMA_ONE = [
  "items",
  "additionalProperties",
  "not",
  "propertyNames",
  "contains",
  "if",
  "then",
  "else",
  "unevaluatedProperties",
  "unevaluatedItems",
  "additionalItems"
];
var SCHEMA_LIST = ["anyOf", "oneOf", "allOf", "prefixItems", "items"];
var SCHEMA_MAP = ["properties", "patternProperties", "$defs", "definitions", "dependentSchemas"];
function mapSubschemas(node, fn) {
  const out = { ...node };
  for (const k of SCHEMA_ONE) {
    const v = node[k];
    if (isNode(v)) out[k] = fn(v);
  }
  for (const k of SCHEMA_LIST) {
    const v = node[k];
    if (Array.isArray(v)) out[k] = v.map((s) => isNode(s) ? fn(s) : s);
  }
  for (const k of SCHEMA_MAP) {
    const v = node[k];
    if (isNode(v))
      out[k] = Object.fromEntries(
        Object.entries(v).map(([name, s]) => [name, isNode(s) ? fn(s) : s])
      );
  }
  return out;
}
function subschemas(node) {
  const found = [];
  mapSubschemas(node, (s) => {
    found.push(s);
    return s;
  });
  return found;
}
function lowerNestedOrdered(node, where) {
  const out = mapSubschemas(node, (s) => lowerNestedOrdered(s, where));
  const rb2 = out.rb;
  if (!isNode(rb2) || rb2.ordered === void 0) return out;
  delete out.rb;
  return keyedMap(out, where);
}
function keyedMap(elem, where) {
  const composite = ["anyOf", "oneOf", "allOf"].filter((k) => elem[k] !== void 0);
  if (elem.type !== "object" || composite.length > 0)
    throw new Error(
      `${where}: rb.ordered element must be a z.object (got ${composite.length > 0 ? composite.join("/") : `type ${JSON.stringify(elem.type)}`})`
    );
  const elemProps = elem.properties ?? {};
  return {
    type: "object",
    additionalProperties: {
      ...elem,
      properties: {
        ...elemProps,
        __order: { type: "string" },
        __deleted: { type: "boolean" }
      }
    }
  };
}
function emitOrderedJsonSchema(inner, where) {
  return keyedMap(emitJsonSchema(inner, where), where);
}
function compileSchema(tables) {
  const manifest = [];
  const relations = { trees: {}, children: {} };
  const tableNames = new Set(Object.keys(tables));
  const refTargets = [];
  const addChild = (parentTable, child) => {
    const list = relations.children[parentTable] ?? [];
    list.push(child);
    relations.children[parentTable] = list;
  };
  for (const [name, table] of Object.entries(tables)) {
    if (name.startsWith("$")) throw new Error(`table name "${name}" may not start with "$"`);
    if (name.startsWith("__"))
      throw new Error(
        `table name "${name}" may not start with "__" (reserved for engine bookkeeping)`
      );
    if (name.includes(":")) throw new Error(`table name "${name}" may not contain ":"`);
    const columns = [];
    for (const [colName, field] of Object.entries(shapeOf(name, table))) {
      if (colName.startsWith("__"))
        throw new Error(
          `column "${name}.${colName}" may not start with "__" (reserved for engine bookkeeping)`
        );
      const { rb: rb2, optional, inner } = resolveColumn(field);
      if (!rb2) {
        throw new Error(
          `column "${name}.${colName}" must use an rb.* type (got a bare Zod schema)`
        );
      }
      if (rb2.role === "id") continue;
      if (rb2.type === "set" && !rb2.elem)
        throw new Error(`column "${name}.${colName}": rb.set() column is missing elem`);
      columns.push({
        name: colName,
        type: rb2.type,
        ...optional ? { isOptional: true } : {},
        ...rb2.indexed ? { isIndexed: true } : {},
        ...rb2.blobRef ? { blobRef: true } : {},
        ...rb2.scope ? { scope: true } : {},
        ...rb2.type === "json" ? {
          jsonSchema: rb2.ordered ? emitOrderedJsonSchema(inner, `${name}.${colName}`) : emitJsonSchema(inner, `${name}.${colName}`)
        } : {},
        ...rb2.ordered ? { ordered: rb2.ordered } : {},
        ...rb2.type === "set" ? { elem: rb2.elem } : {},
        ...rb2.movedFrom ? { movedFrom: rb2.movedFrom } : {},
        ...rb2.transform ? { transform: rb2.transform.toString() } : {}
      });
      if (rb2.parent) {
        relations.trees[name] = { parentKey: colName };
        addChild(rb2.parent, { table: name, fk: colName });
        refTargets.push({ fk: `${name}.${colName}`, target: rb2.parent, kind: "parent" });
      } else if (rb2.ref) {
        addChild(rb2.ref, { table: name, fk: colName });
        refTargets.push({ fk: `${name}.${colName}`, target: rb2.ref, kind: "ref" });
      }
    }
    const vetCols = new Set(columns.map((c) => c.name));
    for (const c of columns) if (c.movedFrom) vetCols.add(c.movedFrom.column);
    for (const c of columns) {
      if (c.transform === void 0) continue;
      const v = vetTransform(c.transform, vetCols);
      if (!v.ok) throw new Error(`${name}.${c.name}: ${v.error}`);
    }
    manifest.push({ name, columns });
  }
  for (const rt of refTargets) {
    if (!tableNames.has(rt.target))
      throw new Error(`rb.${rt.kind} on "${rt.fk}": no such table "${rt.target}"`);
  }
  return { manifest, relations };
}

// src/generate.ts
import { rebuildTempName } from "@jbroll/rowboat-shared";
function byName(xs) {
  return new Map(xs.map((x) => [x.name, x]));
}
function columnMeta(c) {
  return {
    ...c.jsonSchema ? { jsonSchema: c.jsonSchema } : {},
    ...c.blobRef ? { blobRef: true } : {},
    ...c.scope ? { scope: true } : {}
  };
}
function setColumnMeta(table, c, was) {
  return {
    type: "setColumnMeta",
    table,
    column: c.name,
    meta: columnMeta(c),
    previous: columnMeta(was)
  };
}
var sameMeta = (a, b) => JSON.stringify(columnMeta(a)) === JSON.stringify(columnMeta(b));
function generateMigration(prev, next, toVersion, annotations = {}) {
  const warnings = [];
  const adds = [];
  const drops = [];
  const renameTables = annotations.renameTables ?? [];
  const renameColumns = annotations.renameColumns ?? [];
  const prevByName = byName(prev);
  const nextByName = byName(next);
  const prevNameForNext = new Map(renameTables.map((r) => [r.to, r.from]));
  const renamedAwayTables = new Set(renameTables.map((r) => r.from));
  const movedFromSteps = [];
  for (const t of next) {
    const prevTable = prevByName.get(prevNameForNext.get(t.name) ?? t.name);
    const prevCols = new Set((prevTable?.columns ?? []).map((col) => col.name));
    for (const c of t.columns) {
      if (!c.movedFrom) continue;
      if (c.transform !== void 0) {
        const v = vetTransform(c.transform, prevCols);
        if (!v.ok) throw new Error(`${t.name}.${c.name}: ${v.error}`);
        movedFromSteps.push({
          type: "copyFromExpression",
          table: t.name,
          target: { col: c.name },
          sources: v.sources.map((col) => ({ col })),
          source: c.transform
        });
      } else {
        movedFromSteps.push({
          type: "copyFromExpression",
          table: t.name,
          target: { col: c.name },
          sources: [
            { col: c.movedFrom.column, ...c.movedFrom.path ? { path: c.movedFrom.path } : {} }
          ],
          cast: { targetType: c.type, optional: Boolean(c.isOptional) }
        });
      }
    }
  }
  const dataSteps = [...annotations.dataSteps ?? [], ...movedFromSteps];
  for (const r of renameTables) {
    const fromTable = prevByName.get(r.from);
    if (!fromTable) throw new Error(`renameTable: no such table "${r.from}"`);
    if (!nextByName.has(r.to)) throw new Error(`renameTable: "${r.to}" not in the new manifest`);
    const indexed = fromTable.columns.filter((c) => c.isIndexed);
    for (const c of indexed) adds.push({ type: "dropIndex", table: r.from, column: c.name });
    adds.push({ type: "renameTable", from: r.from, to: r.to });
    for (const c of indexed) adds.push({ type: "addIndex", table: r.to, column: c.name });
  }
  for (const t of next) {
    const prevName = prevNameForNext.get(t.name) ?? t.name;
    const p = prevByName.get(prevName);
    if (!p) {
      adds.push({ type: "addTable", def: t });
      continue;
    }
    const renames = renameColumns.filter((rc) => rc.table === t.name);
    const renamedFrom = new Set(renames.map((rc) => rc.from));
    const renamedTo = new Map(renames.map((rc) => [rc.to, rc.from]));
    for (const rc of renames) {
      const fromCol = p.columns.find((c) => c.name === rc.from);
      if (!fromCol) throw new Error(`renameColumn: no such column "${rc.from}" on "${prevName}"`);
      if (!t.columns.some((c) => c.name === rc.to))
        throw new Error(`renameColumn: "${rc.to}" not in the new manifest for "${t.name}"`);
      if (fromCol.isIndexed) adds.push({ type: "dropIndex", table: t.name, column: rc.from });
      adds.push({ type: "renameColumn", table: t.name, from: rc.from, to: rc.to });
    }
    const prevCols = byName(p.columns);
    const nextCols = byName(t.columns);
    for (const c of t.columns) {
      if (renamedTo.has(c.name)) continue;
      if (!prevCols.has(c.name)) adds.push({ type: "addColumn", table: t.name, column: c });
    }
    for (const c of t.columns) {
      const prevColName = renamedTo.get(c.name) ?? c.name;
      const pc = prevCols.get(prevColName);
      if (!pc) continue;
      const renamed = prevColName !== c.name;
      const dropped = { ...pc, name: c.name };
      if (c.type === "counter" || c.type === "set" || pc.type === "counter" || pc.type === "set") {
        if (c.type !== pc.type || !c.isOptional !== !pc.isOptional || c.elem !== pc.elem) {
          if (pc.isIndexed) drops.push({ type: "dropIndex", table: t.name, column: c.name });
          drops.push({ type: "dropColumn", table: t.name, column: c.name, dropped });
          drops.push({ type: "addColumn", table: t.name, column: c });
          if (c.isIndexed) drops.push({ type: "addIndex", table: t.name, column: c.name });
        } else if (!sameMeta(c, pc)) {
          adds.push(setColumnMeta(t.name, c, pc));
        }
        continue;
      }
      if (c.type !== pc.type || !c.isOptional !== !pc.isOptional) {
        const tmpName = rebuildTempName(c.name);
        const { scope: _scope, ...unscoped } = c;
        const tmpCol = { ...unscoped, name: tmpName, isIndexed: false };
        adds.push({ type: "addColumn", table: t.name, column: tmpCol });
        adds.push({
          type: "copyFromExpression",
          table: t.name,
          target: { col: tmpName },
          sources: [{ col: c.name }],
          cast: { targetType: c.type, optional: Boolean(c.isOptional) }
        });
        if (pc.isIndexed && !renamed)
          adds.push({ type: "dropIndex", table: t.name, column: c.name });
        adds.push({ type: "dropColumn", table: t.name, column: c.name, dropped });
        adds.push({ type: "renameColumn", table: t.name, from: tmpName, to: c.name });
        if (c.scope) adds.push(setColumnMeta(t.name, c, tmpCol));
        if (c.isIndexed) adds.push({ type: "addIndex", table: t.name, column: c.name });
        continue;
      }
      if (!sameMeta(c, pc)) adds.push(setColumnMeta(t.name, c, pc));
      if (renamed) {
        if (c.isIndexed) adds.push({ type: "addIndex", table: t.name, column: c.name });
        continue;
      }
      if (c.isIndexed && !pc.isIndexed)
        adds.push({ type: "addIndex", table: t.name, column: c.name });
      if (!c.isIndexed && pc.isIndexed)
        drops.push({ type: "dropIndex", table: t.name, column: c.name });
    }
    for (const c of p.columns) {
      if (renamedFrom.has(c.name)) continue;
      if (!nextCols.has(c.name)) {
        if (c.isIndexed) drops.push({ type: "dropIndex", table: t.name, column: c.name });
        drops.push({ type: "dropColumn", table: t.name, column: c.name, dropped: c });
      }
    }
  }
  for (const p of prev) {
    if (renamedAwayTables.has(p.name)) continue;
    if (!nextByName.has(p.name)) drops.push({ type: "destroyTable", name: p.name, dropped: p });
  }
  const validatedData = [];
  for (const ds of dataSteps) {
    if (ds.sources.length > 1)
      warnings.push(
        `copyFromExpression on ${ds.table}.${ds.target.col}: multi-source (${ds.sources.length}) \u2014 a later offline edit to a losing source can be dropped (design \xA76)`
      );
    validatedData.push(ds);
  }
  return {
    migration: { toVersion, steps: [...adds, ...validatedData, ...drops] },
    warnings
  };
}

// src/movedfrom.ts
import { deepSet, getPath, getPathEntry, UNWRITTEN_FIELD } from "@jbroll/rowboat-shared";
function buildMovedFromFallbacks(manifest, effectiveCols) {
  const map = {};
  for (const table of manifest) {
    const colNames = new Set(table.columns.map((c) => c.name));
    const effective = effectiveCols?.get(table.name);
    const fallbacks = [];
    for (const col of table.columns) {
      if (!col.movedFrom || !colNames.has(col.movedFrom.column)) continue;
      fallbacks.push({
        newCol: col.name,
        oldCol: col.movedFrom.column,
        ...col.movedFrom.path ? { path: col.movedFrom.path } : {},
        mode: effective === void 0 || effective.has(col.name) ? "dual" : "rename"
      });
    }
    if (fallbacks.length > 0) map[table.name] = fallbacks;
  }
  return map;
}
function unwrittenCols(row) {
  const v = row[UNWRITTEN_FIELD];
  if (v === void 0) return EMPTY_COLS;
  if (!Array.isArray(v) || v.some((c) => typeof c !== "string"))
    throw new Error(`row ${String(row.id)}: ${UNWRITTEN_FIELD} is not a list of column names`);
  return new Set(v);
}
var EMPTY_COLS = /* @__PURE__ */ new Set();
function applyMovedFromReads(fallbacks, row, n) {
  if (!fallbacks) return;
  const unwritten = unwrittenCols(row);
  for (const { newCol, oldCol, path } of fallbacks) {
    if (n[newCol] != null && !unwritten.has(newCol)) continue;
    if (path === void 0) {
      if (n[oldCol] != null) n[newCol] = n[oldCol];
    } else {
      const oldJson = n[oldCol];
      if (oldJson != null && typeof oldJson === "object") {
        const v = getPath(oldJson, path);
        if (v != null) n[newCol] = v;
      }
    }
  }
}
function routeMovedFromCreate(table, fallbacks, row) {
  if (!fallbacks) return row;
  let out;
  for (const { newCol, oldCol, path, mode } of fallbacks) {
    if (!(newCol in row)) continue;
    out ??= { ...row };
    if (path === void 0) {
      if (!(oldCol in row)) out[oldCol] = row[newCol];
    } else if (!hasPath(row[oldCol], path)) {
      out[oldCol] = setInCopy(
        out[oldCol],
        path,
        row[newCol],
        () => new Error(
          `create: ${table}.${oldCol} must be an object to carry ${table}.${newCol} at path "${path}"`
        )
      );
    }
    if (mode === "rename") delete out[newCol];
  }
  return out ?? row;
}
var isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
var hasPath = (v, path) => isPlainObject(v) && getPathEntry(v, path).found;
function setInCopy(base, path, value, error) {
  if (base !== void 0 && !isPlainObject(base)) throw error();
  const copy = base === void 0 ? {} : JSON.parse(JSON.stringify(base));
  let cur = copy;
  for (const part of path.split(".").slice(0, -1)) {
    const next = cur[part];
    if (next === void 0) break;
    if (!isPlainObject(next)) throw error();
    cur = next;
  }
  deepSet(copy, path, value);
  return copy;
}
function routeMovedFromWrites(fallbacks, changes) {
  if (!fallbacks) return changes;
  let out;
  for (const { newCol, oldCol, path, mode } of fallbacks) {
    const oldKey = path === void 0 ? oldCol : `${oldCol}.${path}`;
    const keys = Object.keys(changes).filter((k) => k === newCol || k.startsWith(`${newCol}.`));
    if (keys.length === 0) continue;
    out ??= { ...changes };
    const subKeys = keys.filter((k) => k !== newCol);
    assertSubPathsIndependent(subKeys);
    if (newCol in changes && subKeys.length > 0) {
      let whole = changes[newCol];
      for (const key of subKeys)
        whole = setInCopy(
          whole,
          key.slice(newCol.length + 1),
          changes[key],
          () => new Error(`update: "${newCol}" must be an object to carry "${key}"`)
        );
      routeOne(changes, out, newCol, oldKey, whole);
    } else {
      for (const key of keys)
        routeOne(changes, out, key, oldKey + key.slice(newCol.length), changes[key]);
    }
    if (mode === "rename") for (const key of keys) delete out[key];
  }
  return out ?? changes;
}
function assertSubPathsIndependent(keys) {
  const set2 = new Set(keys);
  for (const key of keys) {
    for (let dot = key.lastIndexOf("."); dot > 0; dot = key.lastIndexOf(".", dot - 1)) {
      const outer = key.slice(0, dot);
      if (set2.has(outer))
        throw new Error(
          `update: "${outer}" and "${key}" name a path and a path inside it; write them in separate calls`
        );
    }
  }
}
function routeOne(changes, out, key, target, value) {
  if (target in changes) return;
  const keys = Object.keys(out);
  const inside = keys.find((k) => k.startsWith(`${target}.`));
  if (inside !== void 0)
    throw new Error(
      `update: "${key}" routes to "${target}", and the same update writes "${inside}" inside it; write one or the other`
    );
  let enclosing;
  for (const k of keys) {
    if (target.startsWith(`${k}.`) && (enclosing === void 0 || k.length > enclosing.length))
      enclosing = k;
  }
  if (enclosing === void 0) {
    out[target] = value;
    return;
  }
  const rest = target.slice(enclosing.length + 1);
  if (hasPath(out[enclosing], rest)) {
    if (enclosing in changes) return;
    throw new Error(
      `update: "${key}" routes to "${target}", which "${enclosing}" in the same update already writes; write one or the other`
    );
  }
  const encl = enclosing;
  out[encl] = setInCopy(
    out[encl],
    rest,
    value,
    () => new Error(`update: "${encl}" must be an object to carry "${key}" at "${rest}"`)
  );
}

// src/graph.ts
function stripInternal(row) {
  const out = {};
  for (const k of Object.keys(row)) {
    if (k.startsWith("__")) continue;
    out[k] = row[k];
  }
  return out;
}
function reactiveArrayStore(seed = {}) {
  const base = arrayStore(seed);
  const subs = /* @__PURE__ */ new Set();
  const notify = () => {
    for (const cb of subs) cb();
  };
  return {
    all: base.all,
    create: (t, row) => {
      const live = base.all(t).some((r) => r.id === row.id);
      const p = base.create(t, row);
      if (!live) notify();
      return p;
    },
    update: (t, idOrItems, changes) => {
      const ids = Array.isArray(idOrItems) ? idOrItems.map((it) => it.id) : [idOrItems];
      const anyExisted = base.all(t).some((r) => ids.includes(r.id));
      const p = Array.isArray(idOrItems) ? base.update(t, idOrItems) : base.update(t, idOrItems, changes ?? {});
      if (anyExisted) notify();
      return p;
    },
    remove: (t, id2) => {
      const p = base.remove(t, id2);
      notify();
      return p;
    },
    subscribe: (cb) => {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    }
  };
}
function arrayStore(seed = {}) {
  const tables = {};
  for (const [t, rows] of Object.entries(seed)) tables[t] = [...rows];
  return {
    all: (t) => tables[t] ?? [],
    create: (t, row) => {
      const arr = tables[t] ?? [];
      tables[t] = arr;
      if (arr.some((r) => r.id === row.id))
        return Promise.reject(
          new Error(`create: ${t}/${row.id} already exists; change it with update()`)
        );
      arr.push(row);
      return Promise.resolve();
    },
    update: (t, idOrItems, changes) => {
      const arr = tables[t] ?? [];
      tables[t] = arr;
      const items = Array.isArray(idOrItems) ? idOrItems : [{ id: idOrItems, changes: changes ?? {} }];
      for (const it of items) {
        const i = arr.findIndex((r) => r.id === it.id);
        if (i < 0) return Promise.reject(new Error(`Row not found: ${t}/${it.id}`));
        arr[i] = { ...arr[i], ...it.changes };
      }
      return Promise.resolve();
    },
    remove: (t, id2) => {
      const arr = tables[t];
      if (arr) {
        const i = arr.findIndex((r) => r.id === id2);
        if (i >= 0) arr.splice(i, 1);
      }
      return Promise.resolve();
    }
  };
}
var stripId = (fk) => fk.endsWith("_id") ? fk.slice(0, -3) : fk;
function buildResolvers(relations) {
  const map = {};
  const ensure = (t) => map[t] ??= {};
  for (const [t, { parentKey }] of Object.entries(relations.trees)) {
    ensure(t).parent = {
      target: t,
      kind: "one",
      get: (row, s) => s.all(t).find((r) => r.id === row[parentKey]) ?? null
    };
    ensure(t).children = {
      target: t,
      kind: "many",
      get: (row, s) => s.all(t).filter((r) => r[parentKey] === row.id)
    };
  }
  for (const [parent2, kids] of Object.entries(relations.children)) {
    const fkCount = /* @__PURE__ */ new Map();
    for (const { table } of kids) fkCount.set(table, (fkCount.get(table) ?? 0) + 1);
    for (const { table: child, fk } of kids) {
      if (relations.trees[parent2]?.parentKey === fk && child === parent2) continue;
      const fwd = (fkCount.get(child) ?? 0) > 1 ? `${child}_by_${stripId(fk)}` : child;
      ensure(parent2)[fwd] = {
        target: child,
        kind: "many",
        get: (row, s) => s.all(child).filter((r) => r[fk] === row.id)
      };
      ensure(child)[stripId(fk)] = {
        target: parent2,
        kind: "one",
        get: (row, s) => s.all(parent2).find((r) => r.id === row[fk]) ?? null
      };
    }
  }
  return map;
}
function relational(schema, store, compiled, effectiveCols) {
  const resolved = compiled ?? compileSchema(schema);
  const relations = resolved.relations;
  const resolvers = buildResolvers(relations);
  const movedFromFallbacks = buildMovedFromFallbacks(resolved.manifest, effectiveCols);
  const protos = {};
  const node = (table, row) => {
    const n = Object.assign(Object.create(proto(table)), stripInternal(row));
    applyMovedFromReads(movedFromFallbacks[table], row, n);
    return n;
  };
  const requireMutable = () => {
    const m = store;
    if (!m.create || !m.update || !m.remove) throw new Error("store is read-only");
    return store;
  };
  const run = (fn) => Promise.resolve().then(fn);
  function proto(table) {
    const existing = protos[table];
    if (existing) return existing;
    const p = {};
    for (const [name, r] of Object.entries(resolvers[table] ?? {})) {
      Object.defineProperty(p, name, {
        enumerable: false,
        // G1: writable so a row column whose name equals a resolver (edge tables name refs `child`/
        // `parent`/`from`/`to`, and any non-`_id` ref) SHADOWS the resolver via node()'s
        // Object.assign instead of throwing "Cannot assign to read only property". The raw fk id then
        // wins for that name (resolve the endpoint with g.<target>(id)); `_id`-suffixed refs keep
        // both — the column `owner_id` and the resolver `owner` don't collide.
        writable: true,
        value() {
          const res = r.get(this, store);
          if (r.kind === "many") return res.map((row) => node(r.target, row));
          return res ? node(r.target, res) : null;
        }
      });
    }
    Object.defineProperty(p, "$table", { enumerable: false, value: table });
    Object.defineProperty(p, "$data", {
      enumerable: false,
      get() {
        return { ...this };
      }
    });
    Object.defineProperty(p, "$closure", {
      enumerable: false,
      value(relation) {
        if (!resolvers[table]?.[relation])
          throw new Error(`$closure: no relation "${relation}" on ${table}`);
        const seen = /* @__PURE__ */ new Map();
        let frontier = [this];
        while (frontier.length > 0) {
          const nextFrontier = [];
          for (const cur of frontier) {
            const curTable = cur.$table ?? table;
            const res = resolvers[curTable]?.[relation];
            if (!res) continue;
            const got = res.get(cur, store);
            const rows = Array.isArray(got) ? got : got ? [got] : [];
            for (const row of rows) {
              if (!seen.has(row.id)) {
                const n = node(res.target, row);
                seen.set(row.id, n);
                nextFrontier.push(n);
              }
            }
          }
          frontier = nextFrontier;
        }
        return [...seen.values()];
      }
    });
    protos[table] = p;
    return p;
  }
  const graph = {
    $store: store,
    $table: (name) => graph[name]
  };
  for (const table of Object.keys(schema)) {
    graph[table] = Object.assign(
      (id2) => {
        const row = store.all(table).find((r) => r.id === id2);
        return row ? node(table, row) : void 0;
      },
      {
        all: () => store.all(table).map((r) => node(table, r)),
        // `run` defers to a microtask so any thrown WRITE error (row-not-found, read-only store)
        // becomes a rejected promise. KNOWN GAP (2026-07 review): despite rb.json's inner schema, no
        // client write path runs Zod — a malformed value persists here and is only rejected server-side.
        // Adding client-side validation is a deferred design call (perf + which layer enforces it).
        // Route the row through the create twin of update()'s movedFrom cap-aware routing: dual-write
        // the old column (expand) so un-migrated peers see the new row, or emit only the old column
        // (capped/rename) so a ship-ahead client can create at all instead of hitting assertKnownColumn.
        create: (row) => run(
          () => requireMutable().create(
            table,
            routeMovedFromCreate(table, movedFromFallbacks[table], row)
          )
        ),
        update: (idOrItems, changes) => run(() => {
          const fallbacks = movedFromFallbacks[table];
          return Array.isArray(idOrItems) ? requireMutable().update(
            table,
            idOrItems.map((it) => ({
              id: it.id,
              changes: routeMovedFromWrites(fallbacks, it.changes)
            }))
          ) : requireMutable().update(
            table,
            idOrItems,
            routeMovedFromWrites(fallbacks, changes ?? {})
          );
        }),
        remove: (id2) => run(() => requireMutable().remove(table, id2)),
        move: (id2, parentId) => run(() => {
          const tree = relations.trees[table];
          if (!tree) throw new Error(`${table} is not a tree`);
          const existing = store.all(table).find((r) => r.id === id2);
          if (!existing) throw new Error(`${table} "${id2}" not found`);
          if (parentId !== null) {
            const descendants = node(table, existing).$closure("children");
            if (parentId === id2 || descendants.some((d) => d.id === parentId)) {
              throw new Error("move would create a cycle");
            }
          }
          return requireMutable().update(table, id2, { [tree.parentKey]: parentId });
        })
      }
    );
  }
  return graph;
}

// src/rb.ts
import { add, inc, remove } from "@jbroll/rowboat-shared";
import { z as z2 } from "zod";
function withMeta(base, meta, opts) {
  return base.meta({ rb: { ...meta, ...opts?.index ? { indexed: true } : {} } });
}
var id = () => withMeta(z2.string(), { type: "text", role: "id" });
var text = (opts) => withMeta(z2.string(), { type: "text" }, opts);
var media = (opts) => withMeta(
  z2.object({
    hash: z2.string().meta({ rb: { type: "text", blobRef: true } }),
    size: z2.number().int().nonnegative()
  }),
  { type: "json" },
  opts
);
var int = (opts) => withMeta(z2.number().int(), { type: "integer" }, opts);
var real = (opts) => withMeta(z2.number(), { type: "real" }, opts);
var bool = (opts) => withMeta(z2.boolean(), { type: "boolean" }, opts);
var counter = (opts) => withMeta(z2.number().int(), { type: "counter" }, opts);
var set = (elem, opts) => {
  const elemType = elem instanceof z2.ZodString ? "text" : "integer";
  return withMeta(
    z2.array(elem).readonly(),
    {
      type: "set",
      elem: elemType
    },
    opts
  );
};
var json = (inner) => inner.meta({ rb: { type: "json" } });
var ordered = (element, opts) => element.meta({ rb: { type: "json", ordered: { key: opts.key } } });
var movedFrom = (base, column, opts) => {
  const existing = base.meta()?.rb;
  if (!existing) {
    throw new Error(
      "rb.movedFrom: base column must already carry rb.* meta (got a bare Zod schema)"
    );
  }
  const path = typeof opts === "string" ? opts : opts?.path;
  const transform = typeof opts === "string" ? void 0 : opts?.transform;
  return base.meta({
    rb: {
      ...existing,
      movedFrom: { column, ...path ? { path } : {} },
      ...transform ? { transform } : {}
    }
  });
};
var ref = (table, opts) => withMeta(z2.string(), { type: "text", ref: table }, opts);
var parent = (table) => z2.string().nullable().meta({ rb: { type: "text", parent: table, indexed: true } });
var scope = (opts) => withMeta(z2.string(), { type: "text", scope: true }, { index: opts?.index ?? true });
var rb = {
  id,
  text,
  media,
  int,
  real,
  bool,
  json,
  ordered,
  movedFrom,
  counter,
  set,
  ref,
  parent,
  scope,
  inc,
  add,
  remove
};
export {
  arrayStore,
  compileSchema,
  generateMigration,
  rb,
  reactiveArrayStore,
  relational
};
